import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Tab } from 'react-bootstrap';
import InfoBar from '../components/Navigation/InfoBar';
import useApi from '../hooks/use-api';
import { getPlannedTrainingsForCompany } from '../constants/routes';
import PlannedTrainingModal from '../components/Modal/PlannedTrainingModal/PlannedTrainingModal';
import { transformTrainingDataForCompany } from '../utils/training';
import EnrollEmployeeModal from '../components/Modal/EnrollEmployeesModal';
import moment from '../moment';
import {
	getDateFormat,
	getStartOfDayFullDateFormat,
	getTimeDateFormat,
	sortByDate,
} from '../utils/date';
import ReservationModal from '../components/Modal/ReservationModal/ReservationModal';
import TabsHeader from '../components/Tabs/TabsHeader';
import { useTabs } from '../hooks/use-tabs';
import {
	CERTIFICATES_ENABLED,
	CODE_95_ENABLED,
	useViewPreferences,
} from '../hooks/view-preferences';
import { useUser } from '../hooks/use-user';
import PlanningOverview from '../components/Tables/CourseList/Planning/PlanningOverview';
import { getDisplayName } from '../utils/person';
import useEstablishments from '../hooks/use-establishments';

const PLANNING_OVERVIEW_TAB = 'planning_overview_tab';

// These must match with the tab translation key
const FUTURE_PLANNING_TAB = 'future';
const PAST_PLANNING_TAB = 'past';

const tabs = [FUTURE_PLANNING_TAB, PAST_PLANNING_TAB];

function PlanningPage() {
	const { t } = useTranslation();
	const { activeCompany, isSchoolAdmin, isSchoolSuperUser } = useUser();
	const { key, handleKeyChange } = useTabs(tabs, PLANNING_OVERVIEW_TAB);
	const { isPreferenceOptionEnabled } = useViewPreferences();
	const establishments = useEstablishments();

	const [pastBody, setPastBody] = useState([]);
	const [futureBody, setFutureBody] = useState([]);
	const [training, setTraining] = useState({});
	const [showModal, setShowModal] = useState(false);
	const [showEnrollModal, setShowEnrollModal] = useState(false);
	const [showReservationModal, setShowReservationModal] = useState(false);
	const [selectedTraining, setSelectedTraining] = useState(null);

	const [{ data: pastData }, fetchPastTrainings] = useApi.get(
		getPlannedTrainingsForCompany(activeCompany.id),
		{ startDateBefore: getStartOfDayFullDateFormat(moment()) },
	);
	const [{ data: futureData }, fetchFutureTrainings] = useApi.get(
		getPlannedTrainingsForCompany(activeCompany.id),
		{ startDateAfter: getStartOfDayFullDateFormat(moment()) },
	);

	const handleDisenrollment = () => {
		if (!_.isEmpty(training)) {
			const updatedTraining = futureData.find((newTraining) => newTraining.id === training.id);
			setTraining(
				updatedTraining || {
					...training,
					enrollments: [],
				},
			);
		}
	};

	useEffect(() => {
		if (pastData) {
			setPastBody(transformTrainingDataForCompany(pastData));
		}
	}, [pastData]);

	useEffect(() => {
		if (futureData) {
			setFutureBody(transformTrainingDataForCompany(futureData));
			handleDisenrollment();
		}
	}, [futureData]);

	const prepareXlSX = () => {
		let planningExport = [];
		const sortedPlanningEntries = pastBody.sort((a, b) =>
			sortByDate(a.start, b.start, { order: 'desc' }),
		);
		sortedPlanningEntries.forEach((training) => {
			training.slots.forEach(({ courseName, startDate, end, location, students }) => {
				const companyStudentsEnrollments = training.enrollments.filter(
					(enrollment) => enrollment.company.id === activeCompany.id,
				);
				const companyStudentEntries = students.reduce((carry, student) => {
					if (
						student.establishment?.id &&
						!establishments.find((establishment) => establishment.id === student.establishment.id)
					) {
						return carry;
					}
					const studentEnrollment = companyStudentsEnrollments.find(
						(enrollment) => enrollment.student.id === student.id,
					);
					if (studentEnrollment) {
						const certificateExpirationDate = studentEnrollment?.certificateExpirationDate
							? getDateFormat(studentEnrollment.certificateExpirationDate)
							: '';

						carry.push({
							...(isPreferenceOptionEnabled(CODE_95_ENABLED) && {
								[t('trainings.export_fields.ccv_code')]: training.ccvCode,
							}),
							[t('trainings.export_fields.course_name')]: training.name,
							[t('trainings.export_fields.slot_name')]: courseName,
							[t('trainings.export_fields.date')]: getDateFormat(startDate),
							[t('trainings.export_fields.start')]: getTimeDateFormat(startDate),
							[t('trainings.export_fields.end')]: getTimeDateFormat(end),
							[t('trainings.export_fields.location')]: location,
							[t('trainings.export_fields.student')]: getDisplayName(student),
							...(isPreferenceOptionEnabled(CERTIFICATES_ENABLED) && {
								[t('trainings.export_fields.certificateExpirationDate')]: certificateExpirationDate,
							}),
						});
					}
					return carry;
				}, []);
				planningExport = [...planningExport, ...companyStudentEntries];
			});
		});
		return [
			{
				name: t('navigation.companyUser.planning'),
				rows: planningExport,
			},
		];
	};

	const handleSubmit = () => {
		fetchFutureTrainings();
		fetchPastTrainings();
	};

	const showDisenroll = key === FUTURE_PLANNING_TAB && !(isSchoolAdmin || isSchoolSuperUser);

	return (
		<>
			<InfoBar />
			<EnrollEmployeeModal
				show={showEnrollModal}
				setShow={setShowEnrollModal}
				training={selectedTraining}
				handleSubmit={handleSubmit}
			/>
			<PlannedTrainingModal
				show={showModal}
				setShow={setShowModal}
				training={training}
				showDisenroll={showDisenroll}
				handleSubmit={fetchFutureTrainings}
			/>
			<ReservationModal
				show={showReservationModal}
				setShow={setShowReservationModal}
				training={selectedTraining}
				setTraining={setSelectedTraining}
				handleSubmit={handleSubmit}
			/>
			<div className="row pb-5">
				<div className="col-sm-12 col-xl-10 mx-auto">
					<Tab.Container
						id="planning-tabs"
						className="col-12 col-xl-10 mx-auto my-5"
						activeKey={key}
						onSelect={(k) => handleKeyChange(k)}>
						<TabsHeader
							options={{ className: 'mb-3', i18nPrefix: 'tabs' }}
							tabKey={key}
							tabs={tabs}
						/>
						<Tab.Content>
							<Tab.Pane eventKey={FUTURE_PLANNING_TAB}>
								{futureData === null ? (
									<div className="loading-spinner" />
								) : (
									<>
										<PlanningOverview
											data={futureBody}
											isFuturePlanningList={true}
											onEnrollClick={(training) => {
												setSelectedTraining(training);
												setShowEnrollModal(true);
											}}
											onReserveClick={(training) => {
												setSelectedTraining(training);
												setShowReservationModal(true);
											}}
											handleRowClick={(training) => {
												setTraining(training);
												setShowModal(true);
											}}
										/>
									</>
								)}
							</Tab.Pane>
							<Tab.Pane eventKey={PAST_PLANNING_TAB}>
								{pastData === null ? (
									<div className="loading-spinner" />
								) : (
									<>
										<PlanningOverview
											data={pastBody}
											isFuturePlanningList={false}
											handleRowClick={(training) => {
												setTraining(training);
												setShowModal(true);
											}}
											prepareDownload={prepareXlSX}
										/>
									</>
								)}
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</div>
			</div>
		</>
	);
}

export default PlanningPage;
